// ── Contest platform API client (EX-PUB08) ──────────────────────────────────
// `docs/29` §10, verbatim. Two namespaces and one rule that separates them:
// `/v1/contests/*` reads are soft-authenticated (a `public` contest page is
// marketing before it is a competition, so a stranger must be able to read the
// page that asks them to sign up) and `/v1/contest-platform/*` is authenticated
// with contest *scope* - no platform permission, which is the whole of what
// `EX-PUB05` bought.
//
// Nothing here sends a `contextType`: a contest submission's priority band is
// the route's literal, never a body field (`docs/27` §8.4).

import { downloadAuthedFile, fetchAuthedFile, fetcher, type ApiEnvelope, type AuthedFile } from "@/lib/api";
import { laneRoute, runThroughLane } from "@/features/judge/lib/run-lane";
import { trackChokh } from "@/lib/chokh";
import { getApiBaseUrl } from "@/lib/api-base-url";
import type { RunSampleInput, SampleRunResult } from "@/features/judge/types";
import { problemSetPdfQuery, type ProblemPdfOptions, type ProblemSetPdfOptions } from "./lib/problem-set-pdf";
import type {
  StandingsView,
  ContestHallGuide,
  ContestVenueLock,
  ContestVenueLockInput,
  ContestVenueMyNetwork,
  ContestVenueRefusals,
  ContestVenueRoomCode,
  ExtendContestInput,
  ContestAnnouncementsResponse,
  ContestCertificateEmailResult,
  ContestCertificateList,
  ContestCertificateSettingsInput,
  ContestClarificationsResponse,
  ContestDirectoryKind,
  ContestDirectoryScope,
  ContestPersonInput,
  ContestPoolResponse,
  ContestStaffInput,
  ContestStaffInviteResult,
  ContestStaffListRole,
  ContestProblemSetDownloadsResponse,
  ContestProblemView,
  ContestRatingResponse,
  ContestRegisterResult,
  ContestStandingsResponse,
  ContestRehearsalDetail,
  ContestRehearsalsResponse,
  ContestSubmitInput,
  ContestSubmitResult,
  CreateContestInput,
  ContestFieldSubmissionListQuery,
  ContestFieldSubmissionSource,
  ContestFieldSubmissionsResponse,
  ContestOwnSubmissionSource,
  ContestVirtualAttempt,
  ContestVirtualStandingsResponse,
  MyVirtualRoundsResponse,
  ContestSubmissionListQuery,
  MyContestSubmissionsResponse,
  MyContestSubmissionsEverywhereQuery,
  MyContestSubmissionsEverywhereResponse,
  OrganizerClarificationsResponse,
  ClarificationDraftResponse,
  OrganizerContest,
  OrganizerContestDetail,
  OrganizerContestListResponse,
  OrganizerEventPack,
  PackAddonHoldings,
  SetterAiCreditsView,
  ContestStampAddonItem,
  OrganizerOnsiteKit,
  OrganizerParticipantsResponse,
  OrganizerProblemSetResponse,
  ContestProblemCheckResponse,
  OrganizerSubmissionDetail,
  OrganizerSubmissionsQuery,
  OrganizerSubmissionsResponse,
  OrganizerTeamsResponse,
  ParticipantStatusResult,
  PublicContestDetail,
  PublicContestListResponse,
  PublicContestRound,
  RatingApplyResult,
  RatingReplayResult,
  RejudgeInput,
  RejudgeResult,
  ContestReadiness,
  ContestReport,
  ContestBroadcast,
  ContestBrandInput,
  ContestBrandRead,
  MyTalentPass,
  ContestSponsorSheet,
  ContestInstitutions,
  ContestSeasons,
  EditorSeason,
  PublicSeason,
  SeasonInput,
  SeasonListItem,
  ContestFeed,
  ContestFeedMade,
  LiveLayer,
  LiveLayerPayload,
  ContestTimingReport,
  ContestSimilarityPairKey,
  ContestSimilarityPairReport,
  ContestSimilarityReport,
  ContestIntegrityRecord,
  ContestIntegrityTimeline,
  ContestIntegrityReport,
  StatementDecoyTrace,
  ContestTelemetryEvent,
  UpdateContestInput,
  UpdateContestTeamInput,
  AssignContestSeatInput,
  ContestRoom,
  ContestSeat,
  ContestVenue,
  ContestVenueResponse,
  ContestPrintJob,
  ContestPrintJobPdf,
  ContestPrintQueueResponse,
  ContestPrintStationResponse,
  ContestPrintStatus,
  OwnPrintJobsResponse,
  RequestPrintInput,
  RequestPrintResponse,
  CreateContestRoomInput,
  ContestBalloon,
  ContestBalloonBoardResponse,
  ContestBalloonStatus,
  WorkspaceSnapshotBody,
  WorkspaceSnapshotReceipt,
  WorkspaceSnapshotView,
  ContestReveal,
  ContestHoldsView,
  EditorOnlyStanding,
} from "./types";
import type {
  MyRegistrationAnswers,
  RegistrationAnswerInput,
  RegistrationFile,
  RegistrationQuestionInput,
  TeamRegistrationAnswers,
  TeamRegistrationQuestions,
} from "./registration";

function unwrap<T>(res: ApiEnvelope<T>): T {
  if (!res.success) throw new Error(res.error.message);
  return res.data;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

const slugPath = (slug: string) => `/v1/contests/${encodeURIComponent(slug)}`;
const platformPath = (id: string) => `/v1/contest-platform/contests/${encodeURIComponent(id)}`;

async function post<T>(path: string, body?: unknown): Promise<T> {
  return unwrap(
    await fetcher<T>(path, {
      method: "POST",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  );
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  return unwrap(await fetcher<T>(path, { method: "PATCH", body: JSON.stringify(body) }));
}

async function put<T>(path: string, body: unknown): Promise<T> {
  return unwrap(await fetcher<T>(path, { method: "PUT", body: JSON.stringify(body) }));
}

async function del<T>(path: string): Promise<T> {
  return unwrap(await fetcher<T>(path, { method: "DELETE" }));
}

/* ── The contestant's side ────────────────────────────────────────────────── */

export async function fetchPublicContests(params: {
  scope?: ContestDirectoryScope;
  page?: number;
  limit?: number;
  kind?: ContestDirectoryKind;
  /** `EX-DIV01`: one division's rounds, a preset id or `open` / `custom`. */
  division?: string;
  /** `EX-DIV01`: rated rounds whose band holds the signed-in reader's rating. */
  ratedForMe?: boolean;
}): Promise<PublicContestListResponse> {
  return unwrap(
    await fetcher<PublicContestListResponse>(
      `/v1/contests${buildQuery({
        scope: params.scope,
        page: params.page,
        limit: params.limit,
        kind: params.kind,
        division: params.division,
        ratedForMe: params.ratedForMe ? "true" : undefined,
      })}`,
    ),
  );
}

export async function fetchPublicContest(slug: string): Promise<PublicContestDetail> {
  const data = await fetcher<{ contest: PublicContestDetail }>(slugPath(slug));
  return unwrap(data).contest;
}

/**
 * `EX-DIV04`, `docs/29` §8.1: a round of division contests in one window. Soft
 * authenticated, so a signed-in reader's own placement rides on the same read.
 */
export async function fetchPublicRound(slug: string): Promise<PublicContestRound> {
  return unwrap(await fetcher<PublicContestRound>(`/v1/contest-rounds/${encodeURIComponent(slug)}`));
}

export async function registerForContest(
  slug: string,
  input: { passphrase?: string; acceptedRules?: boolean; answers?: RegistrationAnswerInput[] },
): Promise<ContestRegisterResult> {
  const result = await post<ContestRegisterResult>(`${slugPath(slug)}/register`, {
    ...(input.passphrase ? { passphrase: input.passphrase } : {}),
    ...(input.acceptedRules !== undefined ? { acceptedRules: input.acceptedRules } : {}),
    // `EX-REG01`: only when the contest asks, so an older server never meets the field.
    ...(input.answers && input.answers.length > 0 ? { answers: input.answers } : {}),
  });
  trackChokh("contest_register", { slug });
  return result;
}

export async function fetchContestProblem(
  slug: string,
  letter: string,
): Promise<ContestProblemView> {
  const data = await fetcher<{ problem: ContestProblemView }>(
    `${slugPath(slug)}/problems/${encodeURIComponent(letter)}`,
  );
  return unwrap(data).problem;
}

/** 202 - queued in the contest band, exactly as a practice submission is queued in its own. */
export async function submitToContest(
  slug: string,
  letter: string,
  input: ContestSubmitInput,
): Promise<ContestSubmitResult> {
  return post<ContestSubmitResult>(
    `${slugPath(slug)}/problems/${encodeURIComponent(letter)}/submissions`,
    input,
  );
}

/**
 * The caller's own submissions in this contest.
 *
 * There is no by-id contest submission read and there should not be: the list
 * is the poll, and it is already scoped to "mine" server-side with no parameter
 * for whose it could be.
 */
/** `EX-PUB16`: the query string both submission lists take, empty when nothing is set. */
function submissionListSearch(query: ContestFieldSubmissionListQuery = {}): string {
  const params = new URLSearchParams();
  if (query.letter) params.set("letter", query.letter);
  // `EX-SCB05`: whose rows, on the field list only (its fetcher alone takes a
  // query that can carry them).
  if (query.userId) params.set("userId", query.userId);
  if (query.teamId) params.set("teamId", query.teamId);
  if (query.verdict) params.set("verdict", query.verdict);
  if (query.languageKey) params.set("languageKey", query.languageKey);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));
  const search = params.toString();
  return search ? `?${search}` : "";
}

export async function fetchMyContestSubmissions(
  slug: string,
  query: ContestSubmissionListQuery = {},
): Promise<MyContestSubmissionsResponse> {
  return unwrap(
    await fetcher<MyContestSubmissionsResponse>(
      `${slugPath(slug)}/submissions${submissionListSearch(query)}`,
    ),
  );
}

/**
 * `EX-TST01`: a staff member's run before the round opens (`docs/29` §4.1).
 * The contest submit's body and its 202; the route is its own, so the contest
 * submit keeps one meaning.
 */
/**
 * `EX-EDR02`: run the samples, or one custom input, inside the contest
 * (`docs/29` §6.1). Feedback only: no submission row, nothing on the
 * standings, the submit's gates in the submit's order. Since `EX-SRN01` it
 * waits its turn for a share of the judge (a contestant's on the share a live
 * round keeps), and `JUDGE_ENGINE_BUSY` means only that it waited a minute.
 */
export async function runContestCode(
  slug: string,
  letter: string,
  input: RunSampleInput,
): Promise<SampleRunResult> {
  return runThroughLane(() =>
    post<SampleRunResult>(laneRoute(`${slugPath(slug)}/problems/${encodeURIComponent(letter)}/runs`), input),
  );
}

/* ── A virtual try (`EX-VRT01`, `docs/29` §6.22) ───────────────────────────── */

/** Start the clock; a running try is answered, so a second press starts nothing. */
export async function startVirtualRound(slug: string): Promise<{ attempt: ContestVirtualAttempt }> {
  return post<{ attempt: ContestVirtualAttempt }>(`${slugPath(slug)}/virtual`);
}

/** "Finish now": the clock stops for good and the result is written. */
export async function finishVirtualRound(slug: string): Promise<{ attempt: ContestVirtualAttempt }> {
  return post<{ attempt: ContestVirtualAttempt }>(`${slugPath(slug)}/virtual/finish`);
}

export async function submitVirtual(slug: string, letter: string, input: ContestSubmitInput): Promise<ContestSubmitResult> {
  // A virtual try composes no authorship record, and the route is strict.
  const { languageKey, languageVersion, sourceCode } = input;
  return post<ContestSubmitResult>(`${slugPath(slug)}/virtual/problems/${encodeURIComponent(letter)}/submissions`, {
    languageKey,
    languageVersion,
    sourceCode,
  });
}

export async function runVirtualCode(slug: string, letter: string, input: RunSampleInput): Promise<SampleRunResult> {
  return runThroughLane(() =>
    post<SampleRunResult>(laneRoute(`${slugPath(slug)}/virtual/problems/${encodeURIComponent(letter)}/runs`), input),
  );
}

/**
 * The reader's own virtual rows, in the own list's shape. A person with a
 * virtual try at a round never competed in it, so on that round this is the
 * whole of "my submissions".
 */
export async function fetchMyVirtualSubmissions(
  slug: string,
  query: ContestSubmissionListQuery = {},
): Promise<MyContestSubmissionsResponse> {
  const params = new URLSearchParams();
  if (query.letter) params.set("letter", query.letter);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));
  const search = params.toString();
  const data = unwrap(
    await fetcher<Omit<MyContestSubmissionsResponse, "phase" | "isRegistered" | "isCompeting" | "participantStatus">>(
      `${slugPath(slug)}/virtual/submissions${search ? `?${search}` : ""}`,
    ),
  );
  return { phase: "unfrozen", isRegistered: false, isCompeting: false, participantStatus: null, ...data };
}

export async function fetchMyVirtualSubmissionSource(slug: string, submissionId: string): Promise<ContestOwnSubmissionSource> {
  return unwrap(
    await fetcher<ContestOwnSubmissionSource>(
      `${slugPath(slug)}/virtual/submissions/${encodeURIComponent(submissionId)}/source`,
    ),
  );
}

export async function fetchVirtualStandings(slug: string, page = 1): Promise<ContestVirtualStandingsResponse> {
  return unwrap(
    await fetcher<ContestVirtualStandingsResponse>(`${slugPath(slug)}/virtual/standings${page > 1 ? `?page=${page}` : ""}`),
  );
}

export async function fetchMyVirtualRounds(): Promise<MyVirtualRoundsResponse> {
  return unwrap(await fetcher<MyVirtualRoundsResponse>("/v1/me/virtual-rounds"));
}

export async function submitRehearsal(
  slug: string,
  letter: string,
  input: ContestSubmitInput,
): Promise<ContestSubmitResult> {
  return post<ContestSubmitResult>(
    `${slugPath(slug)}/problems/${encodeURIComponent(letter)}/rehearsals`,
    input,
  );
}

/** `EX-TST01`: every staff member's rehearsals on this round, newest first. */
export async function fetchContestRehearsals(
  slug: string,
  query: { letter?: string; page?: number; limit?: number } = {},
): Promise<ContestRehearsalsResponse> {
  const params = new URLSearchParams();
  if (query.letter) params.set("letter", query.letter);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));
  const search = params.toString();
  return unwrap(
    await fetcher<ContestRehearsalsResponse>(
      `${slugPath(slug)}/rehearsals${search ? `?${search}` : ""}`,
    ),
  );
}

/** `EX-TST01`: one rehearsal with every test's verdict, never its data or the source. */
export async function fetchContestRehearsal(
  slug: string,
  submissionId: string,
): Promise<ContestRehearsalDetail> {
  const data = await fetcher<{ rehearsal: ContestRehearsalDetail }>(
    `${slugPath(slug)}/rehearsals/${encodeURIComponent(submissionId)}`,
  );
  return unwrap(data).rehearsal;
}

/**
 * The field's list, which the server refuses until the scoreboard is open.
 *
 * `/submissions/all` rather than a parameter on the route above, so the one that
 * promises "own, always" keeps that promise in its shape (`docs/29` §10).
 */
export async function fetchContestFieldSubmissions(
  slug: string,
  query: ContestFieldSubmissionListQuery = {},
): Promise<ContestFieldSubmissionsResponse> {
  return unwrap(
    await fetcher<ContestFieldSubmissionsResponse>(
      `${slugPath(slug)}/submissions/all${submissionListSearch(query)}`,
    ),
  );
}

/** `EX-SRC01`: one program from the field list, once the round is published. */
export async function fetchContestFieldSubmissionSource(
  slug: string,
  submissionId: string,
): Promise<ContestFieldSubmissionSource> {
  return unwrap(
    await fetcher<ContestFieldSubmissionSource>(
      `${slugPath(slug)}/submissions/all/${encodeURIComponent(submissionId)}/source`,
    ),
  );
}

/**
 * `EX-SCB05`: one of the reader's own programs, for the standings popup on
 * their own row. Anybody else's id is a 404 (`docs/29` §10).
 */
/**
 * `EX-SCB09`: the caller's own contest submissions across every contest, for
 * My Submissions' Contests tab. Own only: the route takes no "whose".
 */
export async function fetchMyContestSubmissionsEverywhere(
  query: MyContestSubmissionsEverywhereQuery = {},
): Promise<MyContestSubmissionsEverywhereResponse> {
  const params = new URLSearchParams();
  if (query.contestId) params.set("contestId", query.contestId);
  if (query.verdict) params.set("verdict", query.verdict);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));
  const search = params.toString();
  return unwrap(
    await fetcher<MyContestSubmissionsEverywhereResponse>(`/v1/me/contest-submissions${search ? `?${search}` : ""}`),
  );
}

export async function fetchMyContestSubmissionSource(
  slug: string,
  submissionId: string,
): Promise<ContestOwnSubmissionSource> {
  return unwrap(
    await fetcher<ContestOwnSubmissionSource>(
      `${slugPath(slug)}/submissions/${encodeURIComponent(submissionId)}/source`,
    ),
  );
}

export async function fetchContestClarifications(
  slug: string,
): Promise<ContestClarificationsResponse> {
  return unwrap(await fetcher<ContestClarificationsResponse>(`${slugPath(slug)}/clarifications`));
}

export async function askContestClarification(
  slug: string,
  input: { letter?: string; question: string },
): Promise<unknown> {
  return post<unknown>(`${slugPath(slug)}/clarifications`, {
    ...(input.letter ? { letter: input.letter } : {}),
    question: input.question,
  });
}

export async function fetchContestAnnouncements(
  slug: string,
): Promise<ContestAnnouncementsResponse> {
  return unwrap(await fetcher<ContestAnnouncementsResponse>(`${slugPath(slug)}/announcements`));
}

export async function fetchContestStandings(
  slug: string,
  params: {
    view?: StandingsView;
    page?: number;
    limit?: number;
    institution?: string;
    room?: string;
    /** `EX-DIV01`: unofficial rows are left out unless asked for (Wave 29 decision 4). */
    unofficial?: "show";
  } = {},
): Promise<ContestStandingsResponse> {
  return unwrap(
    await fetcher<ContestStandingsResponse>(
      `${slugPath(slug)}/standings${buildQuery({
        view: params.view,
        page: params.page,
        limit: params.limit,
        // `EX-SCB01`: one institution's rows. The server ignores it unless the
        // contest shows institutions, so the panel only offers it then.
        institution: params.institution,
        // `EX-ONS01`: one room's rows, under the same rule for seats.
        room: params.room,
        unofficial: params.unofficial,
      })}`,
    ),
  );
}

/* ── The organizer's side ─────────────────────────────────────────────────── */

export async function fetchOrganizerContests(params: {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}): Promise<OrganizerContestListResponse> {
  return unwrap(
    await fetcher<OrganizerContestListResponse>(
      `/v1/contest-platform/contests${buildQuery({
        search: params.search,
        status: params.status,
        page: params.page,
        limit: params.limit,
      })}`,
    ),
  );
}

export async function fetchOrganizerContest(id: string): Promise<OrganizerContestDetail> {
  const data = await fetcher<{ contest: OrganizerContestDetail }>(platformPath(id));
  return unwrap(data).contest;
}

export async function createContest(input: CreateContestInput): Promise<OrganizerContest> {
  const data = await post<{ contest: OrganizerContest }>(
    `/v1/contest-platform/contests`,
    input,
  );
  return data.contest;
}

export async function updateContest(
  id: string,
  input: UpdateContestInput,
): Promise<OrganizerContestDetail> {
  const data = await patch<{ contest: OrganizerContestDetail }>(platformPath(id), input);
  return data.contest;
}

export async function deleteContest(id: string): Promise<unknown> {
  return del<unknown>(platformPath(id));
}

/**
 * `EX-PRC01`: an optional `eventPackId`, and nothing else.
 *
 * The body carries a handle to a pack the caller already holds, never terms.
 * Price, participant allowance, validity and the bundle flags are all resolved
 * server-side from the pack's own snapshot — the same rule the payment-intent
 * flow has always had (`docs/23` §5.2), applied at the hop where the number
 * becomes an entitlement.
 */
export async function scheduleContest(
  id: string,
  input: { eventPackId?: string; useOnsiteKit?: boolean; useOrgAllowance?: boolean } = {},
): Promise<OrganizerContestDetail> {
  const data = await post<{ contest: OrganizerContestDetail }>(
    `${platformPath(id)}/schedule`,
    input,
  );
  return data.contest;
}

/** The caller's own unconsumed Event Packs, soonest to expire first. */
/** `EX-PKG03`: the caller's unspent add-ons on a pack and their setters' AI credits. */
export async function fetchMyPackAddons(): Promise<PackAddonHoldings> {
  return unwrap(await fetcher<PackAddonHoldings>("/v1/contest-platform/pack-addons"));
}

/** `EX-PKG03`: spend one of the owner's add-ons on this pack contest. */
export async function spendPackAddon(id: string, item: ContestStampAddonItem): Promise<OrganizerContestDetail> {
  const data = await post<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/pack-addons`, { item });
  return data.contest;
}

/** `EX-PKG03`: the setters' AI credits on this contest. */
export async function fetchSetterAiCredits(id: string): Promise<SetterAiCreditsView> {
  return unwrap(await fetcher<SetterAiCreditsView>(`${platformPath(id)}/setter-ai-credits`));
}

/** `EX-PKG03`: the owner gives `amount` credits to an author or a tester of this contest. */
export async function giveSetterAiCredits(id: string, input: { userId: string; amount: number }): Promise<SetterAiCreditsView> {
  return post<SetterAiCreditsView>(`${platformPath(id)}/setter-ai-credits/give`, input);
}

export async function fetchMyEventPacks(): Promise<OrganizerEventPack[]> {
  const data = await fetcher<{ packs: OrganizerEventPack[] }>("/v1/contest-platform/event-packs");
  return unwrap(data).packs ?? [];
}

/** The caller's own unconsumed Onsite Kits, soonest to expire first (`EX-PKG02`). */
export async function fetchMyOnsiteKits(): Promise<OrganizerOnsiteKit[]> {
  const data = await fetcher<{ kits: OrganizerOnsiteKit[] }>("/v1/contest-platform/onsite-kits");
  return unwrap(data).kits ?? [];
}

export async function unscheduleContest(id: string): Promise<OrganizerContestDetail> {
  const data = await post<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/unschedule`);
  return data.contest;
}

/**
 * `EX-DAY05`: the reveal on the projector, a read the moderators alone may
 * make (`moderate`). Its last press is {@link unfreezeContest}, unchanged.
 */
export async function fetchContestReveal(id: string): Promise<ContestReveal> {
  return unwrap(await fetcher<ContestReveal>(`${platformPath(id)}/reveal`));
}

/** The reveal, and it is terminal - nothing takes a revealed scoreboard back. */
export async function unfreezeContest(id: string): Promise<OrganizerContestDetail> {
  const data = await post<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/unfreeze`);
  return data.contest;
}

/**
 * `EX-EXT01`: give a started contest back the minutes an outage took. The
 * owner's route; the server refuses a rated contest here (`rated_operator_only`).
 */
export async function extendContest(id: string, input: ExtendContestInput): Promise<OrganizerContestDetail> {
  const data = await post<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/extend`, input);
  return data.contest;
}

/**
 * `EX-SRC01`, ADR-0082 decision 3: publish the round's submissions, or close
 * them. The server refuses it (409 `not_revealed`) until the list is open.
 */
export async function setSubmissionsPublished(id: string, published: boolean): Promise<OrganizerContestDetail> {
  const data = await put<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/submissions-published`, {
    published,
  });
  return data.contest;
}

/* ── `EX-STF01`, `EX-COL07`: staff and the pool (`docs/29` §4.1, §4.2, §10) ─ */

const STAFF_PATH: Readonly<Record<ContestStaffListRole, string>> = {
  moderator: "moderators",
  author: "authors",
  tester: "testers",
  volunteer: "volunteers",
};

/**
 * `EX-COL07`, ADR-0122: an add to any of the four lists is an invitation the
 * person accepts, so it answers 201 with the detail as it now stands (the
 * person is not on a list yet) and the invitation. `lead` belongs to the
 * author route only; the server refuses it on the others, so it is never sent
 * there.
 */
export async function inviteContestStaff(
  id: string,
  role: ContestStaffListRole,
  person: ContestStaffInput,
  lead = false,
): Promise<ContestStaffInviteResult> {
  return post<ContestStaffInviteResult>(`${platformPath(id)}/${STAFF_PATH[role]}`, {
    ...person,
    ...(role === "author" && lead ? { lead: true } : {}),
  });
}

/**
 * `EX-COL07`: withdraw an open staff invitation or the ownership offer, the
 * owner's act. The answer is the detail without it, and the invitation as
 * `cancelled`.
 */
export async function cancelContestInvitation(
  id: string,
  invitationId: string,
): Promise<ContestStaffInviteResult> {
  return del<ContestStaffInviteResult>(`${platformPath(id)}/invitations/${encodeURIComponent(invitationId)}`);
}

export async function setContestAuthorLead(
  id: string,
  userId: string,
  lead: boolean,
): Promise<OrganizerContestDetail> {
  const data = await patch<{ contest: OrganizerContestDetail }>(
    `${platformPath(id)}/authors/${encodeURIComponent(userId)}`,
    { lead },
  );
  return data.contest;
}

export async function removeContestAuthor(
  id: string,
  userId: string,
): Promise<OrganizerContestDetail> {
  const data = await del<{ contest: OrganizerContestDetail }>(
    `${platformPath(id)}/authors/${encodeURIComponent(userId)}`,
  );
  return data.contest;
}

export async function removeContestTester(
  id: string,
  userId: string,
): Promise<OrganizerContestDetail> {
  const data = await del<{ contest: OrganizerContestDetail }>(
    `${platformPath(id)}/testers/${encodeURIComponent(userId)}`,
  );
  return data.contest;
}

/** `EX-ONS03`: the volunteer list, removed the way the two lists above are. */
export async function removeContestVolunteer(
  id: string,
  userId: string,
): Promise<OrganizerContestDetail> {
  const data = await del<{ contest: OrganizerContestDetail }>(
    `${platformPath(id)}/volunteers/${encodeURIComponent(userId)}`,
  );
  return data.contest;
}

/** `staff_read`: the offers, with each problem's gate state. */
export async function fetchContestPool(id: string): Promise<ContestPoolResponse> {
  return unwrap(await fetcher<ContestPoolResponse>(`${platformPath(id)}/pool`));
}

/** An author's own act: offer one of their problems into the round. */
export async function offerProblemToPool(id: string, problemId: string): Promise<ContestPoolResponse> {
  return post<ContestPoolResponse>(`${platformPath(id)}/pool`, { problemId });
}

export async function withdrawProblemFromPool(
  id: string,
  problemId: string,
): Promise<ContestPoolResponse> {
  return del<ContestPoolResponse>(`${platformPath(id)}/pool/${encodeURIComponent(problemId)}`);
}

/** The rounds the caller is an author of, newest first; self-scoped like `event-packs`. */
export { fetchAuthoring } from "./authoring-query";

export async function removeContestModerator(
  id: string,
  userId: string,
): Promise<OrganizerContestDetail> {
  const data = await del<{ contest: OrganizerContestDetail }>(
    `${platformPath(id)}/moderators/${encodeURIComponent(userId)}`,
  );
  return data.contest;
}

/**
 * `EX-COL07`: an offer, no longer a transfer. The owner changes only when the
 * moderator accepts, so this answers the detail as it is now (with
 * `ownershipOffer` set) and the invitation, `role: "owner"`.
 */
export async function transferContestOwnership(
  id: string,
  userId: string,
): Promise<ContestStaffInviteResult> {
  return post<ContestStaffInviteResult>(`${platformPath(id)}/transfer-ownership`, { userId });
}

export async function fetchOrganizerProblemSet(id: string): Promise<OrganizerProblemSetResponse> {
  return unwrap(await fetcher<OrganizerProblemSetResponse>(`${platformPath(id)}/problems`));
}

/**
 * `EX-SFX02`: what the next save would say about each of these problems,
 * asked before Save. At most `CONTEST_PROBLEM_CHECK_MAX` ids.
 */
export async function fetchContestProblemCheck(
  id: string,
  problemIds: readonly string[],
): Promise<ContestProblemCheckResponse> {
  const ids = problemIds.map((problemId) => encodeURIComponent(problemId)).join(",");
  return unwrap(await fetcher<ContestProblemCheckResponse>(`${platformPath(id)}/problems/check?ids=${ids}`));
}

/**
 * The whole ordered set in one write - add, remove and reorder are one
 * operation, so two moderators cannot interleave into a set neither asked for.
 */
export async function setContestProblems(
  id: string,
  problemIds: string[],
  problemMaxScores?: { problemId: string; maxScore: number }[],
  problemBalloonColors?: { problemId: string; hex: string; name: string }[],
): Promise<OrganizerProblemSetResponse> {
  return put<OrganizerProblemSetResponse>(`${platformPath(id)}/problems`, {
    problemIds,
    // `EX-CFS01`: sent only by the format that reads them. Omitted, every row
    // keeps the value it already had (`docs/29` §6.3).
    ...(problemMaxScores ? { problemMaxScores } : {}),
    // `EX-ONS03`: the same rule for the balloon colours.
    ...(problemBalloonColors ? { problemBalloonColors } : {}),
  });
}

/* ── `EX-PDF02`: the problem set on paper (`docs/07` "The problem set on paper") ── */

/** `GET .../problem-set/pdf`: the whole set; no options is the one-press PDF. */
export function problemSetPdfPath(id: string, options: ProblemSetPdfOptions = {}): string {
  return `${platformPath(id)}/problem-set/pdf${problemSetPdfQuery(options)}`;
}

/** `GET .../problems/:letter/pdf`: one problem, no cover. */
export function problemPdfPath(id: string, letter: string, options: ProblemPdfOptions = {}): string {
  return `${platformPath(id)}/problems/${encodeURIComponent(letter)}/pdf${problemSetPdfQuery(options)}`;
}

/**
 * `EX-PDF05`, decision 8: the set from the contest page, for anyone signed in
 * once a public round has ended. The one press, so no option is sent.
 */
export function publicProblemSetPdfPath(slug: string): string {
  return `${slugPath(slug)}/problem-set/pdf`;
}

/** A problem set PDF, read with the bearer and held in memory until it is saved or printed. */
export async function fetchContestPdf(path: string, fallbackFilename: string): Promise<AuthedFile> {
  return fetchAuthedFile(path, fallbackFilename);
}

/** Who downloaded the set, newest first (`moderate`: the owner and the moderators). */
export async function fetchProblemSetDownloads(
  id: string,
  params: { page?: number; limit?: number } = {},
): Promise<ContestProblemSetDownloadsResponse> {
  return unwrap(
    await fetcher<ContestProblemSetDownloadsResponse>(`${platformPath(id)}/problem-set/downloads${buildQuery(params)}`),
  );
}

export async function fetchOrganizerParticipants(
  id: string,
  params: { page?: number; limit?: number; status?: string; teamId?: string } = {},
): Promise<OrganizerParticipantsResponse> {
  return unwrap(
    await fetcher<OrganizerParticipantsResponse>(
      `${platformPath(id)}/participants${buildQuery({
        page: params.page,
        limit: params.limit,
        status: params.status,
        teamId: params.teamId,
      })}`,
    ),
  );
}

/* ── `EX-REG01`: registration questions, answers and files ─────────────── */

/** One file for a file question, before the answer that names it is saved. */
export async function uploadRegistrationFile(slug: string, questionId: string, file: File): Promise<RegistrationFile> {
  const form = new FormData();
  form.append("file", file, file.name);
  const data = await fetcher<{ file: RegistrationFile }>(
    `${slugPath(slug)}/registration/files${buildQuery({ questionId })}`,
    { method: "POST", body: form },
  );
  return unwrap(data).file;
}

export async function fetchMyRegistrationAnswers(slug: string): Promise<MyRegistrationAnswers> {
  return unwrap(await fetcher<MyRegistrationAnswers>(`${slugPath(slug)}/registration/answers`));
}

export async function updateMyRegistrationAnswers(
  slug: string,
  answers: RegistrationAnswerInput[],
): Promise<MyRegistrationAnswers> {
  return put<MyRegistrationAnswers>(`${slugPath(slug)}/registration/answers`, { answers });
}

export async function fetchRegistrationQuestions(id: string): Promise<TeamRegistrationQuestions> {
  return unwrap(await fetcher<TeamRegistrationQuestions>(`${platformPath(id)}/registration-questions`));
}

export async function saveRegistrationQuestions(
  id: string,
  questions: RegistrationQuestionInput[],
): Promise<TeamRegistrationQuestions> {
  return put<TeamRegistrationQuestions>(`${platformPath(id)}/registration-questions`, { questions });
}

export async function fetchRegistrationAnswers(id: string, userIds: readonly string[]): Promise<TeamRegistrationAnswers> {
  return unwrap(
    await fetcher<TeamRegistrationAnswers>(
      `${platformPath(id)}/registration-answers${buildQuery({ userIds: userIds.join(",") })}`,
    ),
  );
}

/**
 * The team's five-minute link to one file (an audit row each time). The
 * server answers a path; the API origin goes in front of it here.
 */
export async function openRegistrationFile(id: string, fileId: string): Promise<void> {
  const link = await post<{ path: string; expiresAt: string }>(
    `${platformPath(id)}/registration-files/${encodeURIComponent(fileId)}/link`,
  );
  window.location.assign(`${getApiBaseUrl()}${link.path}`);
}

/**
 * `HR-EXP01`, `docs/29` §6.2: the whole roster as a CSV file, with the bearer.
 * The fields are the list's own; the server names the file.
 */
export async function downloadContestParticipantsCsv(id: string): Promise<void> {
  await downloadAuthedFile(`${platformPath(id)}/participants.csv`, "participants.csv");
}

/**
 * `HR-EXP01`: the whole board as a CSV file, in the view the reader has on
 * screen. The server caps it exactly as it caps that board, so a frozen view's
 * file holds no result after the freeze.
 */
export async function downloadContestStandingsCsv(
  id: string,
  view: "auto" | "frozen" | "public",
): Promise<void> {
  await downloadAuthedFile(
    `${platformPath(id)}/standings.csv${buildQuery({ view: view === "auto" ? undefined : view })}`,
    "standings.csv",
  );
}

/** `AD-DSH05`: by username or by id, the staff lists' shape. */
export async function inviteContestParticipant(id: string, person: ContestPersonInput): Promise<unknown> {
  return post<unknown>(`${platformPath(id)}/participants`, person);
}

export async function removeContestParticipant(id: string, userId: string): Promise<unknown> {
  return del<unknown>(`${platformPath(id)}/participants/${encodeURIComponent(userId)}`);
}

export type ParticipantAction = "approve" | "block" | "disqualify" | "reinstate";

/**
 * Four named transitions, never one `PATCH .../status`: each is a different
 * decision with its own audit action, and the reason is the moderator's own
 * words shown back to support in a dispute.
 */
export async function moveContestParticipant(
  id: string,
  userId: string,
  action: ParticipantAction,
  reason?: string,
): Promise<ParticipantStatusResult> {
  return post<ParticipantStatusResult>(
    `${platformPath(id)}/participants/${encodeURIComponent(userId)}/${action}`,
    reason ? { reason } : {},
  );
}

export async function fetchOrganizerTeams(id: string): Promise<OrganizerTeamsResponse> {
  return unwrap(await fetcher<OrganizerTeamsResponse>(`${platformPath(id)}/teams`));
}

export async function createContestTeam(
  id: string,
  input: { name: string; memberIds?: string[] },
): Promise<unknown> {
  return post<unknown>(`${platformPath(id)}/teams`, input);
}

/**
 * `EX-SCB01`: a rename and the team's metadata are one route. `{ name }` alone
 * is the rename it always was; the institution, the logo id, the country and
 * the roster ride the same PATCH from the team panel.
 */
export async function updateContestTeam(
  id: string,
  teamId: string,
  input: UpdateContestTeamInput,
): Promise<unknown> {
  return patch<unknown>(`${platformPath(id)}/teams/${encodeURIComponent(teamId)}`, input);
}

/* ── The venue (`EX-ONS01`, `docs/29` §6.2, §10) ─────────────────────────── */

const venuePath = (id: string) => `${platformPath(id)}/venue`;

export async function fetchContestVenue(id: string): Promise<ContestVenueResponse> {
  return unwrap(await fetcher<ContestVenueResponse>(venuePath(id)));
}

export async function putContestVenue(id: string, input: { name: string }): Promise<{ venue: ContestVenue }> {
  return put<{ venue: ContestVenue }>(venuePath(id), input);
}

export async function createContestRoom(
  id: string,
  input: CreateContestRoomInput,
): Promise<{ room: ContestRoom; seats: ContestSeat[] }> {
  return post<{ room: ContestRoom; seats: ContestSeat[] }>(`${venuePath(id)}/rooms`, input);
}

export async function renameContestRoom(id: string, roomId: string, input: { name: string }): Promise<{ room: ContestRoom }> {
  return patch<{ room: ContestRoom }>(`${venuePath(id)}/rooms/${encodeURIComponent(roomId)}`, input);
}

export async function deleteContestRoom(id: string, roomId: string): Promise<{ deleted: boolean; seats: number }> {
  return del<{ deleted: boolean; seats: number }>(`${venuePath(id)}/rooms/${encodeURIComponent(roomId)}`);
}

export async function addContestRoomSeats(id: string, roomId: string, input: { labels: string[] }): Promise<{ seats: ContestSeat[] }> {
  return post<{ seats: ContestSeat[] }>(`${venuePath(id)}/rooms/${encodeURIComponent(roomId)}/seats`, input);
}

export async function assignContestSeat(id: string, seatId: string, input: AssignContestSeatInput): Promise<{ seat: ContestSeat }> {
  return put<{ seat: ContestSeat }>(`${venuePath(id)}/seats/${encodeURIComponent(seatId)}/assignment`, input);
}

export async function releaseContestSeat(id: string, seatId: string): Promise<{ seat: ContestSeat }> {
  return del<{ seat: ContestSeat }>(`${venuePath(id)}/seats/${encodeURIComponent(seatId)}/assignment`);
}

export async function setContestSeatStatus(id: string, seatId: string, status: "free" | "blocked"): Promise<{ seat: ContestSeat }> {
  return patch<{ seat: ContestSeat }>(`${venuePath(id)}/seats/${encodeURIComponent(seatId)}`, { status });
}

export async function spreadContestSeats(id: string, input: { reset?: boolean }): Promise<{ placed: number; unseated: number; reset: number }> {
  return post<{ placed: number; unseated: number; reset: number }>(`${venuePath(id)}/spread`, input);
}

export async function deleteContestTeam(id: string, teamId: string): Promise<unknown> {
  return del<unknown>(`${platformPath(id)}/teams/${encodeURIComponent(teamId)}`);
}

/** Adding a member registers them, through the `invite` door - one pass, not two. */
export async function addContestTeamMember(
  id: string,
  teamId: string,
  person: ContestPersonInput,
): Promise<unknown> {
  return post<unknown>(`${platformPath(id)}/teams/${encodeURIComponent(teamId)}/members`, person);
}

export async function removeContestTeamMember(
  id: string,
  teamId: string,
  userId: string,
): Promise<unknown> {
  return del<unknown>(
    `${platformPath(id)}/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
  );
}

export async function moveContestTeam(
  id: string,
  teamId: string,
  action: "disqualify" | "reinstate",
  reason?: string,
): Promise<unknown> {
  return post<unknown>(
    `${platformPath(id)}/teams/${encodeURIComponent(teamId)}/${action}`,
    reason ? { reason } : {},
  );
}

export async function fetchClarificationQueue(
  id: string,
  params: { status?: string; page?: number; limit?: number; roomId?: string } = {},
): Promise<OrganizerClarificationsResponse> {
  return unwrap(
    await fetcher<OrganizerClarificationsResponse>(
      `${platformPath(id)}/clarifications${buildQuery({
        status: params.status,
        page: params.page,
        limit: params.limit,
        // `EX-ONS01`: one room's questions.
        roomId: params.roomId,
      })}`,
    ),
  );
}

/* ── The Clarification Assistant (`EX-CLA01`, `docs/29` §6.2) ─────────────── */

export function newClarificationAssistKey(): string {
  return `cla_${crypto.randomUUID()}`;
}

/** Group with AI: sorts the waiting questions into groups on the queue. */
export async function groupClarifications(
  id: string,
  idempotencyKey: string,
): Promise<{ groups: number; grouped: number; payer: "included" | "credits" }> {
  return post(`${platformPath(id)}/clarifications/group`, { idempotencyKey });
}

/** A draft answer for one group; never stored, never sent. */
export async function draftClarificationAnswer(
  id: string,
  input: { clarificationIds: string[]; idempotencyKey: string },
): Promise<ClarificationDraftResponse> {
  return post(`${platformPath(id)}/clarifications/draft`, input);
}

export async function splitClarification(id: string, clarificationId: string): Promise<unknown> {
  return post<unknown>(`${platformPath(id)}/clarifications/${encodeURIComponent(clarificationId)}/split`, {});
}

/** One answer to every question of a group: the staff send. */
export async function answerClarificationGroup(
  id: string,
  input: {
    clarificationIds: string[];
    answer: string;
    visibility: "asker" | "public";
    aiAssisted: boolean;
    /** The Waiting view's send: a row another judge answered meanwhile is left as it is. */
    onlyWaiting: boolean;
  },
): Promise<{ answered: number; skipped?: number }> {
  return post(`${platformPath(id)}/clarifications/answer-group`, input);
}

export async function fetchOrganizerAnnouncements(
  id: string,
): Promise<ContestAnnouncementsResponse> {
  return unwrap(await fetcher<ContestAnnouncementsResponse>(`${platformPath(id)}/announcements`));
}

export async function postContestAnnouncement(
  id: string,
  input: { title?: string; body: string },
): Promise<unknown> {
  return post<unknown>(`${platformPath(id)}/announcements`, input);
}

export async function updateContestAnnouncement(
  id: string,
  announcementId: string,
  input: { title?: string; body?: string },
): Promise<unknown> {
  return patch<unknown>(
    `${platformPath(id)}/announcements/${encodeURIComponent(announcementId)}`,
    input,
  );
}

export async function deleteContestAnnouncement(
  id: string,
  announcementId: string,
): Promise<unknown> {
  return del<unknown>(`${platformPath(id)}/announcements/${encodeURIComponent(announcementId)}`);
}

export async function fetchOrganizerSubmissions(
  id: string,
  params: OrganizerSubmissionsQuery = {},
): Promise<OrganizerSubmissionsResponse> {
  return unwrap(
    await fetcher<OrganizerSubmissionsResponse>(
      `${platformPath(id)}/submissions${buildQuery({
        letter: params.letter,
        userId: params.userId,
        teamId: params.teamId,
        verdict: params.verdict,
        status: params.status,
        page: params.page,
        limit: params.limit,
      })}`,
    ),
  );
}

/**
 * One submission, with its source. Deliberately a separate request: reading a
 * contestant's program is an act, not a side effect of opening a list.
 */
export async function fetchOrganizerSubmission(
  id: string,
  submissionId: string,
): Promise<OrganizerSubmissionDetail> {
  const data = await fetcher<{ submission: OrganizerSubmissionDetail }>(
    `${platformPath(id)}/submissions/${encodeURIComponent(submissionId)}`,
  );
  return unwrap(data).submission;
}

export async function rejudgeContestSubmissions(
  id: string,
  input: RejudgeInput,
): Promise<RejudgeResult> {
  return post<RejudgeResult>(`${platformPath(id)}/rejudge`, input);
}

/* ── The rated request (`EX-RRQ01`, `docs/29` §7.1) ───────────────────────── */

/**
 * The owner asks for the round to be rated, and withdraws while it is undecided.
 *
 * **On the organizer surface, and nothing here sets `rated`** (§7.1). That rule
 * is not softened by these two: promotion stays an administrator act, and this
 * is a signal with a reason. The three conditions (draft or scheduled, not
 * already rated, and §7.4's own predicate) are the server's; the panel mirrors
 * them rather than deciding for itself.
 *
 * After a **decline** the way forward is another `requestContestRated`, which
 * replaces the declined row. There is no withdraw for a decided request: an
 * owner does not get to erase an operator's answer.
 */
export async function requestContestRated(
  id: string,
  reason: string,
): Promise<OrganizerContestDetail> {
  const res = await post<{ contest: OrganizerContestDetail }>(
    `${platformPath(id)}/rated-request`,
    { reason },
  );
  return res.contest;
}

export async function withdrawContestRatedRequest(id: string): Promise<OrganizerContestDetail> {
  const res = await del<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/rated-request`);
  return res.contest;
}

/**
 * `EX-DAY03`, `docs/29` §6.9: the owner books a rehearsal with the Progsity
 * team (or changes a time not yet confirmed), and cancels it. Confirming is an
 * operator's act in admin; this console has no route that could reach it.
 */
export async function bookContestRehearsal(id: string, startAt: string): Promise<OrganizerContestDetail> {
  const res = await post<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/rehearsal-booking`, { startAt });
  return res.contest;
}

export async function cancelContestRehearsal(id: string): Promise<OrganizerContestDetail> {
  const res = await del<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/rehearsal-booking`);
  return res.contest;
}

/**
 * `EX-DAY04`, `docs/29` §6.10: the owner of a pack contest creates its one
 * warm-up. The answer is the warm-up's own detail, a draft to set up.
 */
export async function createContestWarmUp(
  id: string,
  input: { startAt: string; endAt: string },
): Promise<OrganizerContestDetail> {
  const res = await post<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/warm-up`, input);
  return res.contest;
}

/* ── Certificates (`EX-DAY06`, `docs/29` §6.12) ──────────────────────────── */

/** The owner saves the certificates' settings; answers the console's contest. */
export async function saveContestCertificates(
  id: string,
  input: ContestCertificateSettingsInput,
): Promise<OrganizerContestDetail> {
  const res = await put<{ contest: OrganizerContestDetail }>(`${platformPath(id)}/certificates`, input);
  return res.contest;
}

/**
 * `EX-CRT01`: email the certificates once the board is final: without a
 * recipient every one never emailed, with one that person's again.
 */
export async function emailContestCertificates(
  id: string,
  body: { recipientId?: string; resend?: boolean } = {},
): Promise<ContestCertificateEmailResult> {
  return post<ContestCertificateEmailResult>(`${platformPath(id)}/certificates/email`, body);
}

/** A sample with today's settings, marked "SAMPLE" and never stored. */
export async function downloadContestCertificateSample(id: string): Promise<void> {
  await downloadAuthedFile(`${platformPath(id)}/certificates/preview`, "certificate-sample.pdf");
}

/** The certificates this reader may download, and why there are none yet. */
export async function fetchContestCertificates(slug: string): Promise<ContestCertificateList> {
  return unwrap(await fetcher<ContestCertificateList>(`${slugPath(slug)}/certificates`));
}

/** One certificate's PDF, with the bearer; its first download issues it. */
export async function downloadContestCertificate(slug: string, recipientId: string, fallbackName: string): Promise<void> {
  await downloadAuthedFile(`${slugPath(slug)}/certificates/${encodeURIComponent(recipientId)}/pdf`, fallbackName);
}

/* ── Rating (`EX-PUB07`, `docs/29` §7) ────────────────────────────────────── */

/**
 * The ledger, read by anybody who can read the contest.
 *
 * The scoreboard already carries each row's delta for the page it returned, so
 * this is the whole-contest read: the operator console's summary, and the
 * archive view of a finished round.
 */
export async function fetchContestRating(
  slug: string,
  params: { page?: number; limit?: number } = {},
): Promise<ContestRatingResponse> {
  return unwrap(
    await fetcher<ContestRatingResponse>(
      `${slugPath(slug)}/rating${buildQuery({ page: params.page, limit: params.limit })}`,
    ),
  );
}

/**
 * The three writes, all on `/v1/admin/contest-platform/*` and all gated on
 * `contest.manage`.
 *
 * **Not on the organizer surface, deliberately** (`docs/29` §7.1): a public
 * creator who could mark their own contest rated could inflate their friends'
 * ratings in an afternoon, and the number would mean nothing by the end of the
 * month. Owning the contest is not enough here.
 */
const operatorPath = (id: string) =>
  `/v1/admin/contest-platform/contests/${encodeURIComponent(id)}`;

export async function setContestRated(
  id: string,
  rated: boolean,
): Promise<OrganizerContestDetail> {
  const data = await patch<{ contest: OrganizerContestDetail }>(`${operatorPath(id)}/rated`, {
    rated,
  });
  return data.contest;
}

export async function applyContestRating(id: string): Promise<RatingApplyResult> {
  return post<RatingApplyResult>(`${operatorPath(id)}/rating/apply`);
}

/** Recompute this contest and every rated contest after it, oldest first. */
export async function replayContestRating(fromContestId: string): Promise<RatingReplayResult> {
  return post<RatingReplayResult>("/v1/admin/contest-platform/rating/replay", { fromContestId });
}

/** `EX-RDY01`: the Overview's "Ready for the day". `read` scope; computed on request. */
export async function fetchContestReadiness(id: string): Promise<ContestReadiness> {
  return unwrap(await fetcher<ContestReadiness>(`${platformPath(id)}/readiness`));
}

/**
 * The solve-time report (`EX-ANM01`, `docs/29` §6.2). Moderate scope: a
 * contestant is refused, which is why the hook does not retry a 403.
 */
export async function fetchContestTiming(id: string): Promise<ContestTimingReport> {
  return unwrap(await fetcher<ContestTimingReport>(`${platformPath(id)}/timing`));
}

/** Run the detectors now. Idempotent, and it returns the fresh report. */
/* ── `EX-RPT01`: the Round Report (`docs/29` §6.15) ──────────────────────── */

/** The numbers on every plan; `409 CONTEST_REPORT_NOT_READY` before the end. */
export async function fetchContestReport(id: string): Promise<ContestReport> {
  return unwrap(await fetcher<ContestReport>(`${platformPath(id)}/report`));
}

/**
 * One AI write of the summary, from the counts alone. `idempotencyKey` is a
 * new `rps_` key for each press, so a retried request never spends twice.
 */
export async function draftContestReportSummary(
  id: string,
  idempotencyKey: string,
): Promise<{ status: "draft"; en: string; bn: string; retried: boolean }> {
  return post(`${platformPath(id)}/report/summary/draft`, { idempotencyKey });
}

/** The moderator's text, which is what accepts a summary. */
export async function saveContestReportSummary(
  id: string,
  input: { en: string; bn: string; includeInPdf: boolean },
): Promise<{ status: "accepted"; en: string; bn: string; includeInPdf: boolean }> {
  return put(`${platformPath(id)}/report/summary`, input);
}

export async function downloadContestReportPdf(id: string, lang: "en" | "bn"): Promise<void> {
  await downloadAuthedFile(`${platformPath(id)}/report/pdf?lang=${lang}`, `round-report-${lang}.pdf`);
}

/* ── The hall's network guide (`EX-HAL01`, `docs/29` §6.18) ─────────────── */

export async function fetchContestHallGuide(id: string): Promise<ContestHallGuide> {
  return unwrap(await fetcher<ContestHallGuide>(`${platformPath(id)}/hall-guide`));
}

/** Ask once, with an optional note; the owner's act. */
export async function requestContestHallGuide(id: string, note?: string): Promise<ContestHallGuide> {
  return post(`${platformPath(id)}/hall-guide`, note ? { note } : {});
}

/** The accepted guide on A4, in English or Bangla, through `progsity-print`. */
export async function downloadContestHallGuidePdf(id: string, lang: "en" | "bn"): Promise<void> {
  await downloadAuthedFile(`${platformPath(id)}/hall-guide/pdf?lang=${lang}`, `hall-network-guide-${lang}.pdf`);
}

/* ── The venue lock and the room code (`EX-VEN01`, `docs/29` §6.21) ─────── */

export async function fetchContestVenueLock(id: string): Promise<ContestVenueLock> {
  return unwrap(await fetcher<ContestVenueLock>(`${platformPath(id)}/venue-lock`));
}

/** The whole lock, as the panel holds it; the server stores the ranges canonical. */
export async function saveContestVenueLock(id: string, input: ContestVenueLockInput): Promise<ContestVenueLock> {
  return put(`${platformPath(id)}/venue-lock`, {
    networkOn: input.networkOn,
    networks: input.networks.map((n) => ({ name: n.name, cidr: n.cidr, ...(n.addedAt ? { addedAt: n.addedAt } : {}) })),
    roomCodeOn: input.roomCodeOn,
  });
}

/** The caller's own address as the server saw it, for "Use the address I am on now". */
export async function fetchContestVenueMyNetwork(id: string): Promise<ContestVenueMyNetwork> {
  return unwrap(await fetcher<ContestVenueMyNetwork>(`${platformPath(id)}/venue-lock/my-network`));
}

export async function fetchContestVenueRoomCode(id: string): Promise<ContestVenueRoomCode> {
  return unwrap(await fetcher<ContestVenueRoomCode>(`${platformPath(id)}/venue-lock/room-code`));
}

export async function fetchContestVenueRefusals(id: string): Promise<ContestVenueRefusals> {
  return unwrap(await fetcher<ContestVenueRefusals>(`${platformPath(id)}/venue-lock/refusals`));
}

/** Let one issued login in from anywhere, past the networks and the code, or lock it again. */
export async function setContestVenueLetIn(id: string, userId: string, letIn: boolean): Promise<{ changed: boolean }> {
  const path = `${platformPath(id)}/venue-lock/let-in/${encodeURIComponent(userId)}`;
  return letIn ? post(path, {}) : del(path);
}

/* â”€â”€ The live stream overlay (`EX-OVL01`, `docs/29` Â§6.16) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

export async function fetchContestBroadcast(id: string): Promise<ContestBroadcast> {
  return unwrap(await fetcher<ContestBroadcast>(`${platformPath(id)}/broadcast`));
}

/** Make the links, or replace them: every old link stops at once. */
export async function makeContestBroadcastKey(id: string): Promise<ContestBroadcast> {
  return post(`${platformPath(id)}/broadcast/key`);
}

/** `EX-CLI01`: the ICPC feed card. */
export async function fetchContestFeed(id: string): Promise<ContestFeed> {
  return unwrap(await fetcher<ContestFeed>(`${platformPath(id)}/feed`));
}

/** Make the token, or replace it: the old one stops at once. The answer is the only place the token is. */
export async function makeContestFeedToken(id: string): Promise<ContestFeedMade> {
  return post(`${platformPath(id)}/feed/token`);
}

export async function revokeContestFeedToken(id: string): Promise<ContestFeed> {
  return del(`${platformPath(id)}/feed/token`);
}

export async function setContestFeedFinalResults(id: string, finalResults: boolean): Promise<ContestFeed> {
  return patch(`${platformPath(id)}/feed`, { finalResults });
}

export async function setContestBroadcastLayers(
  id: string,
  layers: Partial<Record<LiveLayer, boolean>>,
): Promise<ContestBroadcast> {
  return patch(`${platformPath(id)}/broadcast`, { layers });
}

/**
 * One layer, as OBS reads it. Anonymous on purpose: the key in the path is
 * the credential, and a streaming PC's browser carries nobody's session.
 */
export async function fetchContestLiveLayer(key: string, layer: LiveLayer): Promise<LiveLayerPayload> {
  return unwrap(
    await fetcher<LiveLayerPayload>(
      `/v1/contest-live/${encodeURIComponent(key)}/${layer}`,
      { cache: "no-store" },
      undefined,
      { anonymous: true },
    ),
  );
}

/* ── Sponsors and the round's look (`EX-SPN01`, `docs/29` §6.23) ───────────── */

export async function fetchContestBrand(id: string): Promise<ContestBrandRead> {
  return unwrap(await fetcher<ContestBrandRead>(`${platformPath(id)}/brand`));
}

/** The whole block, replaced in one write. */
export async function saveContestBrand(id: string, brand: ContestBrandInput): Promise<ContestBrandRead> {
  return put(`${platformPath(id)}/brand`, brand);
}

/* ── The Talent Pass (`EX-TAL01`, `docs/29` §6.26) ─────────────────────────── */

export async function fetchMyTalentPass(slug: string): Promise<MyTalentPass> {
  return unwrap(await fetcher<MyTalentPass>(`${slugPath(slug)}/talent-pass`, { cache: "no-store" }));
}

/** Give (the companies named now) or withdraw; the answer is the pass as it stands after. */
export async function setMyTalentPass(slug: string, consent: boolean): Promise<MyTalentPass> {
  return put(`${slugPath(slug)}/talent-pass`, { consent });
}

export async function fetchContestSponsorSheet(id: string): Promise<ContestSponsorSheet> {
  return unwrap(await fetcher<ContestSponsorSheet>(`${platformPath(id)}/sponsor-sheet`, { cache: "no-store" }));
}

/* ── `EX-INS01`: institutions and the season (`docs/29` §6.25) ───────────── */

/** The contest page's Institutions tab, after the end, under the board's own cap. */
export async function fetchContestInstitutions(slug: string): Promise<ContestInstitutions> {
  return unwrap(await fetcher<ContestInstitutions>(`${slugPath(slug)}/institutions`, { cache: "no-store" }));
}

/** A published season's own page. */
export async function fetchPublicSeason(slug: string): Promise<PublicSeason> {
  return unwrap(await fetcher<PublicSeason>(`/v1/contest-seasons/${encodeURIComponent(slug)}`, { cache: "no-store" }));
}

const SEASONS_PATH = "/v1/contest-platform/seasons";

export async function fetchMySeasons(): Promise<SeasonListItem[]> {
  return unwrap(await fetcher<SeasonListItem[]>(SEASONS_PATH, { cache: "no-store" }));
}

export async function fetchSeason(id: string): Promise<EditorSeason> {
  return unwrap(await fetcher<EditorSeason>(`${SEASONS_PATH}/${id}`, { cache: "no-store" }));
}

export async function createSeason(input: SeasonInput): Promise<EditorSeason> {
  return post(SEASONS_PATH, input);
}

/** The whole season replaced in one write; the address stays. */
export async function saveSeason(id: string, input: SeasonInput): Promise<EditorSeason> {
  return put(`${SEASONS_PATH}/${id}`, input);
}

export async function deleteSeason(id: string): Promise<{ deleted: true }> {
  return del(`${SEASONS_PATH}/${id}`);
}

/** The console's Season section: the seasons this round is in. */
export async function fetchContestSeasons(id: string): Promise<ContestSeasons> {
  return unwrap(await fetcher<ContestSeasons>(`${platformPath(id)}/seasons`, { cache: "no-store" }));
}

/**
 * One open of the contest page, for the sponsor sheet. Anonymous and silent:
 * the answer is always 204, and a failure costs nothing but the count.
 */
export async function sendContestPageView(slug: string): Promise<void> {
  try {
    await fetcher<null>(`${slugPath(slug)}/view`, { method: "POST", cache: "no-store" }, undefined, { anonymous: true });
  } catch {
    /* not counted */
  }
}

export async function recomputeContestTiming(id: string): Promise<ContestTimingReport> {
  return post<ContestTimingReport>(`${platformPath(id)}/timing/recompute`);
}

/**
 * The cluster review, with `EX-AUT06`'s authorship column (`docs/29` §6.2).
 *
 * Moderate scope, like the timing report; free on every tier, because the check
 * is the platform's honesty and what is sold is the report (ADR-0060 decision 2).
 * It carries no source code: reading a contestant's program is a separate,
 * deliberate act on the submissions panel.
 */
export async function fetchContestSimilarity(id: string): Promise<ContestSimilarityReport> {
  return unwrap(await fetcher<ContestSimilarityReport>(`${platformPath(id)}/similarity`));
}

/**
 * `EX-CMP01`: one pair from a cluster, both programs and the regions they share.
 * Moderate scope like the cluster review; on a `summary` report `pair` is `null`.
 */
export async function fetchContestSimilarityPair(
  id: string,
  key: ContestSimilarityPairKey,
): Promise<ContestSimilarityPairReport> {
  const query = new URLSearchParams({ problemId: key.problemId, left: key.left, right: key.right });
  return unwrap(
    await fetcher<ContestSimilarityPairReport>(`${platformPath(id)}/similarity/pair?${query.toString()}`),
  );
}

/**
 * `EX-WSP01`: one snapshot of the contest editor, from the problem page. The
 * answer is a receipt; a bounded refusal is a 200 with `accepted: false`.
 */
export async function sendWorkspaceSnapshot(
  slug: string,
  letter: string,
  body: WorkspaceSnapshotBody,
): Promise<WorkspaceSnapshotReceipt> {
  return post<WorkspaceSnapshotReceipt>(
    `${slugPath(slug)}/problems/${encodeURIComponent(letter)}/snapshots`,
    body,
  );
}

/**
 * `EX-WSP01`: one entry's workspace stream for one problem, after `endAt`.
 * Moderate scope; 409 `CONTEST_NOT_ENDED` while the round runs; audited.
 */
export async function fetchWorkspaceSnapshots(
  id: string,
  participantId: string,
  problemId: string,
): Promise<WorkspaceSnapshotView> {
  return unwrap(
    await fetcher<WorkspaceSnapshotView>(
      `${platformPath(id)}/snapshots/${encodeURIComponent(participantId)}/${encodeURIComponent(problemId)}`,
    ),
  );
}

/** One standings entry's integrity record (`EX-AUT06`). Moderate scope. */
export async function fetchContestIntegrityRecord(
  id: string,
  participantId: string,
): Promise<ContestIntegrityRecord> {
  return unwrap(
    await fetcher<ContestIntegrityRecord>(`${platformPath(id)}/integrity/${participantId}`),
  );
}

/**
 * `EX-INT07`: one entry's timeline, moments and accepted submissions in order.
 * Moderate scope, freeze-safe by that scope, `no-store` on the wire.
 */
export async function fetchContestIntegrityTimeline(
  id: string,
  participantId: string,
): Promise<ContestIntegrityTimeline> {
  return unwrap(
    await fetcher<ContestIntegrityTimeline>(`${platformPath(id)}/integrity/${participantId}/timeline`),
  );
}

/**
 * The whole round's integrity report (`EX-INT04`, `docs/29` §6.2). Moderate scope.
 *
 * The header says what was watched and what was not, and it is served whatever
 * the stamp says: only `entries` narrows on a plan without the report, because
 * refusing outright would take the free half with it (`docs/33` §5.5).
 */
export async function fetchContestIntegrityReport(id: string): Promise<ContestIntegrityReport> {
  return unwrap(await fetcher<ContestIntegrityReport>(`${platformPath(id)}/integrity`));
}

/**
 * `EX-EDO01`: who is waiting to be let back in at the Editor only level.
 * Moderate scope, `no-store`; both consoles poll it while the round is open.
 */
export async function fetchContestHolds(id: string): Promise<ContestHoldsView> {
  return unwrap(await fetcher<ContestHoldsView>(`${platformPath(id)}/integrity/holds`));
}

/** `EX-EDO01`: a staff member lets a held contestant back in. Audited on the server. */
export async function releaseContestHold(
  id: string,
  participantId: string,
  userId: string,
): Promise<{ released: boolean }> {
  return unwrap(
    await fetcher<{ released: boolean }>(`${platformPath(id)}/integrity/${participantId}/release`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
  );
}

/**
 * POST /v1/contests/:slug/integrity/presence (`EX-EDO01`): the Editor only
 * page's presence call. It answers the caller's own standing and nothing else;
 * `null` when the server refused it (another level, the window shut).
 */
export async function sendContestPresence(slug: string): Promise<EditorOnlyStanding | null> {
  const res = await fetcher<{ editorOnly?: EditorOnlyStanding }>(`${slugPath(slug)}/integrity/presence`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  if (!res.success) return null;
  return res.data.editorOnly ?? null;
}

/**
 * POST /v1/contest-platform/contests/:id/statement-decoy/trace (`EX-STP01`).
 * The text goes in the body, never a URL: it is somebody's leaked statement.
 */
export async function traceStatementDecoy(id: string, text: string): Promise<StatementDecoyTrace> {
  return unwrap(
    await fetcher<StatementDecoyTrace>(`${platformPath(id)}/statement-decoy/trace`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  );
}

/** The route's field names for one sensor event (`EX-INT04`). */
function toContestWireEvent(event: ContestTelemetryEvent) {
  return {
    type: event.type,
    detail: event.detail,
    // The proctoring module's word for the item is `itemId`; a contest's item is
    // a problem letter.
    letter: event.itemId,
    timestamp: event.timestamp,
    seq: event.seq,
    prevHash: event.prevHash,
    paste: event.paste,
    // `EX-INT07`: a `page_return`'s away time; absent on every other event.
    awayMs: event.awayMs,
  };
}

/**
 * POST /v1/contests/:slug/integrity/events - deliver an **ordered** batch.
 *
 * One request for the whole queue, so the chain's order is explicit in the
 * payload rather than an accident of request scheduling. Returns the chain head
 * after the last event, which the next batch presents as its `prevHash`.
 */
export async function reportContestIntegrityEvents(
  slug: string,
  events: readonly ContestTelemetryEvent[],
  dispatchedSeq?: number,
): Promise<{ seq: number; hash: string | null } | null> {
  if (events.length === 0) return null;
  const res = await fetcher<{ telemetry?: { seq: number; hash: string | null } }>(
    `${slugPath(slug)}/integrity/events`,
    {
      method: "POST",
      body: JSON.stringify({
        events: events.map(toContestWireEvent),
        ...(dispatchedSeq === undefined ? {} : { dispatchedSeq }),
      }),
    },
  );
  if (!res.success) return null;
  return res.data.telemetry ?? null;
}

/**
 * Fire the batch without waiting for, or reading, the answer.
 *
 * `keepalive` is what lets a request outlive the document that started it, which
 * is the whole requirement of a `pagehide` flush. Not `navigator.sendBeacon`: a
 * beacon cannot carry an `Authorization` header and this API accepts no other
 * credential, so a beacon would be answered 401 and the last minutes of a round
 * discarded.
 */
export function contestTelemetryBatchRequest(
  slug: string,
  events: readonly ContestTelemetryEvent[],
  dispatchedSeq: number | undefined,
  opts: { keepalive?: boolean } = {},
): void {
  if (events.length === 0) return;
  void fetcher(`${slugPath(slug)}/integrity/events`, {
    method: "POST",
    body: JSON.stringify({
      events: events.map(toContestWireEvent),
      ...(dispatchedSeq === undefined ? {} : { dispatchedSeq }),
    }),
    ...(opts.keepalive ? { keepalive: true } : {}),
  }).catch(() => {
    // Unacknowledged by construction. The rows stay in IndexedDB and the next
    // page load delivers them.
  });
}

/* ── The print queue (`EX-ONS02`, `docs/29` §6.2, §10) ───────────────────── */

const printPath = (id: string) => `${platformPath(id)}/print`;

/** The contestant's Print button. The response never carries the source. */
export async function requestContestPrint(slug: string, input: RequestPrintInput): Promise<RequestPrintResponse> {
  return post<RequestPrintResponse>(`${slugPath(slug)}/print`, input);
}

export async function fetchOwnPrintJobs(slug: string): Promise<OwnPrintJobsResponse> {
  return unwrap(await fetcher<OwnPrintJobsResponse>(`${slugPath(slug)}/print`));
}

export async function fetchPrintQueue(
  id: string,
  query: { roomId?: string; status?: ContestPrintStatus },
): Promise<ContestPrintQueueResponse> {
  return unwrap(await fetcher<ContestPrintQueueResponse>(`${printPath(id)}${buildQuery(query)}`));
}

export async function fetchPrintStation(id: string, roomId: string): Promise<ContestPrintStationResponse> {
  return unwrap(await fetcher<ContestPrintStationResponse>(`${printPath(id)}/station/${encodeURIComponent(roomId)}`));
}

export async function transitionPrintJob(
  id: string,
  jobId: string,
  status: Exclude<ContestPrintStatus, "queued">,
): Promise<{ job: ContestPrintJob }> {
  return post<{ job: ContestPrintJob }>(`${printPath(id)}/jobs/${encodeURIComponent(jobId)}/status`, { status });
}

/** The paper, rendered by the server on request; the station turns it into a blob. */
export async function fetchPrintJobPdf(id: string, jobId: string): Promise<ContestPrintJobPdf> {
  return unwrap(await fetcher<ContestPrintJobPdf>(`${printPath(id)}/jobs/${encodeURIComponent(jobId)}/pdf`));
}

/* ── The balloons (`EX-ONS03`, `docs/29` §6.2, §10) ──────────────────────── */

const balloonPath = (id: string) => `${platformPath(id)}/balloons`;

/** The board and the runner page read the same payload; `roomId` narrows it to one room. */
export async function fetchBalloonBoard(
  id: string,
  query: { roomId?: string; status?: ContestBalloonStatus },
): Promise<ContestBalloonBoardResponse> {
  return unwrap(await fetcher<ContestBalloonBoardResponse>(`${balloonPath(id)}${buildQuery(query)}`));
}

/** The runner's one move. A refused move means somebody else got there first. */
export async function deliverBalloon(id: string, balloonId: string): Promise<{ balloon: ContestBalloon }> {
  return post<{ balloon: ContestBalloon }>(`${balloonPath(id)}/${encodeURIComponent(balloonId)}/deliver`, {});
}
